import * as React from 'react';

import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { handleError, is500 } from '@/logic/shared';

import { AxiosError } from 'axios';
import { DynamicPageComponent } from '@/components/shared/dynamic-page';
import { DynamicPagesTypes } from '@/constants/dynamic-pages-enum';
import { SeoPagesEnum } from '@/constants/seo-pages-enum';
import { findSeoInsidePage } from '@/logic/helpers/find-seo-inside-page';
import getLangHeaders from '@/logic/helpers/get-lang-headers';
import { useGetDynamicPage } from '@/logic/services';
import { useGetPagesSeoMeta } from '@/logic/services/seo';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';

const TermsAndConditions: NextPage<{
  path: string;
}> = ({ path }) => {
  const { data: seoDataList } = useGetPagesSeoMeta();
  const seoPage = findSeoInsidePage(
    seoDataList?.pageMetaData,
    SeoPagesEnum.termsAndCondition
  );

  const { data: pageData, isLoading } = useGetDynamicPage({
    query: { type: DynamicPagesTypes.termsAndConditions },
  });

  return (
    <DynamicPageComponent
      title='dynamic_pages.terms_and_conditions'
      pageData={pageData}
      isLoading={isLoading}
      seoPage={seoPage}
      path={path}
    />
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const headers = {
    ...getLangHeaders(context.locale),
  };
  const { locale, defaultLocale } = context;
  const queryClient = new QueryClient();

  try {
    await useGetDynamicPage.fetchQuery(queryClient, {
      query: { type: DynamicPagesTypes.termsAndConditions },
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  try {
    await useGetPagesSeoMeta.fetchQuery(queryClient, {
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      path: `${context.locale}${context.resolvedUrl}`,
      intlMessages: await loadIntlMessages(locale as string, defaultLocale),
    },
  };
};
export default TermsAndConditions;
